import { supabase } from './supabase';

/**
 * FONTE ÚNICA DE NAVEGAÇÃO.
 *
 * Toda decisão de "para onde essa pessoa vai" e "onde ela está agora" sai daqui.
 * AuthContext, AdminLayout e as guardas de rota importam deste arquivo — nenhum
 * `/dashboard` ou `/admin` hardcoded sobrevive fora dele.
 *
 * A regra que este arquivo materializa:
 *   - cada perfil tem UMA casa;
 *   - ninguém cai numa área que não é do seu perfil por acidente;
 *   - dentro do painel, todo caminho de "voltar"/"home" leva a /admin — o site
 *     público só é alcançado pelo botão explícito "Ver site", em nova aba.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Papéis e casas
// ─────────────────────────────────────────────────────────────────────────────

/** Valores realmente existentes em `user_roles.role`. Comprador de curso, de
 *  mentoria e de livro não têm papel: são `null`. Não existe papel 'leitor'. */
export type Papel = 'admin' | 'instructor' | null;

/**
 * Papéis de STAFF. Verificado no banco em 2026-08-08: `user_roles` tem
 * admin = 1 e instructor = 1. **`super_admin` NÃO EXISTE** — aparecia em SQL de
 * policy e num filtro do painel do instrutor, sempre sem casar com nada.
 *
 * Staff NÃO gera progresso: quem abre a aula para conferir não pode contaminar
 * percentual, conclusão nem gatilho de certificado com navegação de inspeção.
 * A proibição é imposta pela RLS; isto aqui é o espelho no cliente, para a tela
 * não oferecer um botão que o banco vai recusar.
 */
export const PAPEIS_DE_STAFF = ['admin', 'instructor'] as const;

export function ehStaff(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'instructor';
}

export const HOME_ADMIN = '/admin';
export const HOME_MENTORADO = '/meu-metodo-silex';
export const HOME_ALUNO = '/dashboard';
export const HOME_PUBLICA = '/';

/**
 * Slug do curso canônico da Mentoria SÍLEX em `courses.slug`.
 *
 * É o `:courseId` da rota `/aula/:courseId/:lessonId` quando a aula é da
 * trilha. Os 3 SKUs de venda (`silex-spark`, `silex-lab`, `silex-protag`) são
 * produtos e NÃO existem em `courses` — foi por isso que o card do dashboard
 * levava a `/curso-home/silex-spark` e batia em "Curso não encontrado".
 */
export const SLUG_TRILHA_SILEX = 'mentoria-silex';

/** A aula aberta é da trilha SÍLEX? Decide o destino do "Voltar" na página de
 *  aula: mentorado volta para a trilha, aluno volta para a home do curso. */
export function ehTrilhaSilex(courseId: string | undefined | null): boolean {
  return courseId === SLUG_TRILHA_SILEX;
}

/** Query param que sinaliza "seu perfil ainda não tem área própria". Lido por
 *  AvisoPerfilSemArea; existe para o instrutor não ser despejado, sem
 *  explicação, na área de aluno. */
export const PARAM_AVISO_AREA = 'area';
export const AVISO_INSTRUTOR_SEM_AREA = 'instrutor-sem-area';

/** Rotas de ÁREA LOGADA. Ao restaurar sessão numa destas, o AuthContext NÃO
 *  redireciona — a pessoa já está onde quis estar. Rota nova de área logada
 *  precisa entrar aqui, senão o reload joga o usuário para a home do perfil. */
const PREFIXOS_AREA_LOGADA = [
  '/admin',
  '/dashboard',
  '/meu-metodo-silex',
  '/curso-home',
  '/aula',
  '/avaliacao',
  '/certificados',
  '/meu-perfil',
  '/checkout',
  '/progresso',
  '/conquistas',
];

export function ehAreaLogada(pathname: string): boolean {
  return PREFIXOS_AREA_LOGADA.some((p) => pathname.startsWith(p));
}

/**
 * Casa do perfil, de forma síncrona.
 *
 * `temAcessoSilex` só é conhecido depois de uma chamada ao banco; quando não for
 * informado, mentorado e aluno compartilham /dashboard — que é, por decisão de
 * produto (CONTINUIDADE §4), a superfície COMPARTILHADA ("Minha área"), não uma
 * área de aluno em sentido estrito. Para a versão que distingue os dois, use
 * `resolverHomeDoPerfil`.
 */
export function homeDoPerfil(role: Papel, temAcessoSilex?: boolean): string {
  if (role === 'admin') return HOME_ADMIN;

  // O painel do instrutor foi fechado em 2026-08-03 e a área nova ainda não
  // existe. Mandar para /dashboard seria colocá-lo na área de aluno — o bug que
  // esta regra existe para impedir. Vai para a home pública COM aviso.
  if (role === 'instructor') {
    return `${HOME_PUBLICA}?${PARAM_AVISO_AREA}=${AVISO_INSTRUTOR_SEM_AREA}`;
  }

  if (temAcessoSilex) return HOME_MENTORADO;
  return HOME_ALUNO;
}

/**
 * Casa do perfil consultando o banco quando necessário.
 *
 * Mentorado NÃO é distinguível por papel — quem compra a Mentoria SÍLEX continua
 * com `role = null`, igual a quem compra um curso ou um livro. A distinção vem da
 * matrícula, exposta pela RPC `silex_access_proprio` (a mesma que a trilha usa). Falha na
 * RPC cai para /dashboard: a superfície compartilhada é o destino seguro.
 */
export async function resolverHomeDoPerfil(role: Papel): Promise<string> {
  if (role === 'admin' || role === 'instructor') return homeDoPerfil(role);

  try {
    const { data, error } = await supabase.rpc('silex_access_proprio');
    if (error) return HOME_ALUNO;
    const acesso = (data as { has_access?: boolean }[] | null)?.[0];
    return homeDoPerfil(role, acesso?.has_access === true);
  } catch {
    return HOME_ALUNO;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mapa do painel administrativo
// ─────────────────────────────────────────────────────────────────────────────

export interface RotaAdmin {
  path: string;
  /** Rótulo da tela — vira a última migalha do breadcrumb e o título da topbar. */
  titulo: string;
  /** Linha de apoio da topbar. Descreve o que a tela faz, sem promessa vazia. */
  descricao: string;
  icone: string;
  /** Ausente = a tela não aparece no menu lateral (mas ainda tem breadcrumb). */
  grupo?: string;
}

export interface GrupoAdmin {
  id: string;
  label: string;
  icone: string;
  /** Grupo sem filhos é um item de navegação direto (a home do painel). */
  path?: string;
}

/** Grupos do menu — agrupados pelo MODELO DE NEGÓCIO, não pelas tabelas. */
export const GRUPOS_ADMIN: GrupoAdmin[] = [
  { id: 'home', label: 'Painel', icone: 'ri-dashboard-3-line', path: HOME_ADMIN },
  { id: 'silex', label: 'Mentoria SÍLEX', icone: 'ri-fire-line' },
  { id: 'cursos', label: 'Cursos', icone: 'ri-graduation-cap-line' },
  { id: 'vendas', label: 'Vendas & Pessoas', icone: 'ri-shopping-bag-3-line' },
  { id: 'operacao', label: 'Operação', icone: 'ri-tools-line' },
];

/**
 * Toda rota do painel, com o texto que a topbar mostra.
 *
 * Este array é a ÚNICA definição: o menu lateral, o breadcrumb e o destaque do
 * item ativo saem todos daqui. Tela nova = uma entrada aqui e a rota no router.
 */
export const ROTAS_ADMIN: RotaAdmin[] = [
  {
    path: '/admin',
    titulo: 'Painel',
    descricao: 'O que precisa da sua ação hoje',
    icone: 'ri-dashboard-3-line',
    grupo: 'home',
  },
  {
    path: '/admin/silex/trilha',
    titulo: 'Trilha (48 Etapas)',
    descricao: 'Editar Etapas e aulas, enviar vídeo e material',
    icone: 'ri-route-line',
    grupo: 'silex',
  },
  {
    path: '/admin/cursos',
    titulo: 'Gerenciar Cursos',
    descricao: 'Criar cursos, publicar e estruturar Módulos e aulas',
    icone: 'ri-book-open-line',
    grupo: 'cursos',
  },
  {
    path: '/admin/alunos',
    titulo: 'Alunos Matriculados',
    descricao: 'Progresso e atividade de quem está matriculado',
    icone: 'ri-team-line',
    grupo: 'cursos',
  },
  {
    path: '/admin/pedidos',
    titulo: 'Pedidos',
    descricao: 'Confirmar pagamento e liberar acesso',
    icone: 'ri-file-list-3-line',
    grupo: 'vendas',
  },
  {
    path: '/admin/rastreamento',
    titulo: 'Rastreamento',
    descricao: 'Código de rastreio dos livros físicos',
    icone: 'ri-truck-line',
    grupo: 'vendas',
  },
  {
    path: '/admin/cupons',
    titulo: 'Cupons de Desconto',
    descricao: 'Criar e acompanhar o uso dos cupons',
    icone: 'ri-coupon-3-line',
    grupo: 'vendas',
  },
  {
    path: '/admin/avaliacoes',
    titulo: 'Reviews & Avaliações',
    descricao: 'Moderar as avaliações publicadas pelos alunos',
    icone: 'ri-star-line',
    grupo: 'vendas',
  },
  {
    path: '/admin/webinars',
    titulo: 'Webinars',
    descricao: 'Agendar transmissões e acompanhar inscrições',
    icone: 'ri-live-line',
    grupo: 'operacao',
  },
  {
    path: '/admin/mentoria-slots',
    titulo: 'Mentoria (Slots)',
    descricao: 'Horários disponíveis para as sessões de mentoria',
    icone: 'ri-calendar-check-line',
    grupo: 'operacao',
  },
  {
    path: '/admin/retencao',
    titulo: 'Retenção',
    descricao: 'E-mails automáticos para quem parou de estudar',
    icone: 'ri-user-follow-line',
    grupo: 'operacao',
  },
  {
    path: '/admin/churn',
    titulo: 'Alunos Inativos',
    descricao: 'Quem está em risco de abandonar o curso',
    icone: 'ri-user-unfollow-line',
    grupo: 'operacao',
  },
  {
    path: '/admin/setup',
    titulo: 'Setup do Sistema',
    descricao: 'Verificação de configuração e dados de base',
    icone: 'ri-settings-3-line',
    grupo: 'operacao',
  },
  // Fora do menu — alcançável por link direto, mas ainda com breadcrumb próprio.
  {
    path: '/admin/upload',
    titulo: 'Upload de Livros',
    descricao: 'Enviar o arquivo dos livros digitais',
    icone: 'ri-upload-cloud-2-line',
  },
];

/** Filhos de um grupo, na ordem em que aparecem no menu. */
export function rotasDoGrupo(grupoId: string): RotaAdmin[] {
  return ROTAS_ADMIN.filter((r) => r.grupo === grupoId && r.path !== HOME_ADMIN);
}

/**
 * A rota atual do painel. Casamento exato primeiro; depois o prefixo mais longo,
 * para que uma sub-rota futura (ex.: /admin/cursos/:id) herde o breadcrumb do pai
 * em vez de cair no genérico.
 */
export function rotaAdminAtual(pathname: string): RotaAdmin | null {
  const exata = ROTAS_ADMIN.find((r) => r.path === pathname);
  if (exata) return exata;

  const porPrefixo = ROTAS_ADMIN
    .filter((r) => r.path !== HOME_ADMIN && pathname.startsWith(`${r.path}/`))
    .sort((a, b) => b.path.length - a.path.length);

  return porPrefixo[0] ?? null;
}

export interface Migalha {
  label: string;
  /** Sem path = migalha atual (não é link). */
  path?: string;
}

/**
 * Breadcrumb da tela: Painel / Grupo / Tela.
 *
 * O grupo não é link — é um rótulo de agrupamento do menu, não uma rota. Só
 * "Painel" navega, e navega para dentro do painel. Nunca para o site público.
 */
export function trilhaDeMigalhas(pathname: string): Migalha[] {
  const rota = rotaAdminAtual(pathname);
  if (!rota || rota.path === HOME_ADMIN) return [{ label: 'Painel' }];

  const migalhas: Migalha[] = [{ label: 'Painel', path: HOME_ADMIN }];

  const grupo = GRUPOS_ADMIN.find((g) => g.id === rota.grupo);
  if (grupo && grupo.id !== 'home') migalhas.push({ label: grupo.label });

  migalhas.push({ label: rota.titulo });
  return migalhas;
}
